import { jsx, jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "@crehana/i18n";
import Radio from "../../Radio/index.js";
import { Button } from "../Button/index.js";
import { DropdownValue } from "../Dropdown/index.js";
import { Input } from "../Input/index.js";
import { Popper, PopperContent, PopperTrigger } from "../Popper/index.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select/index.js";
import i18n from "./i18n/index.js";
import { DefaultConditionalTypesEnum } from "./types.js";
const defaultOptions = [
    {
        label: 'DOES_NOT_CONTAIN',
        value: DefaultConditionalTypesEnum.NOT_CONTAINS,
        blockType: 'single'
    },
    {
        label: 'CONTAINS',
        value: DefaultConditionalTypesEnum.CONTAINS,
        blockType: 'single'
    },
    {
        label: 'RANGE',
        value: DefaultConditionalTypesEnum.RANGE,
        blockType: 'double'
    },
    {
        label: 'EQUALS',
        value: DefaultConditionalTypesEnum.EQUAL_TO,
        blockType: 'single',
        select: true
    },
    {
        label: 'ENDS_WITH',
        value: DefaultConditionalTypesEnum.ENDS_WITH,
        blockType: 'single'
    },
    {
        label: 'STARTS_WITH',
        value: DefaultConditionalTypesEnum.BEGINS_WITH,
        blockType: 'single'
    }
];
function RadioItemInput({ blockType, onChange, defaultValue, type }) {
    const { languageCode } = useTranslation();
    const [firstValue, setFirstValue] = useState(defaultValue?.[0] || '');
    const [secondValue, setSecondValue] = useState(defaultValue?.[1] || '');
    const handleInputChange = (value, inputIndex)=>{
        if (0 === inputIndex) {
            setFirstValue(value);
            'double' === blockType ? onChange([
                value,
                secondValue
            ]) : onChange([
                value
            ]);
        } else {
            setSecondValue(value);
            onChange([
                firstValue,
                value
            ]);
        }
    };
    switch(blockType){
        case 'single':
            return /*#__PURE__*/ jsx(Input, {
                className: "tw-w-full",
                autoComplete: "off",
                value: firstValue,
                onChange: (e)=>handleInputChange(e.target.value, 0),
                type: type
            });
        case 'double':
            return /*#__PURE__*/ jsxs("div", {
                className: "tw-flex tw-items-center tw-gap-1 tw-pt-0.8",
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: "tw-max-w-[9rem]",
                        children: /*#__PURE__*/ jsx(Input, {
                            className: "tw-w-full",
                            autoComplete: "off",
                            value: firstValue,
                            onChange: (e)=>handleInputChange(e.target.value, 0),
                            type: type
                        })
                    }),
                    /*#__PURE__*/ jsx("p", {
                        className: "tw-font-body4",
                        children: i18n[languageCode].TO
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: "tw-max-w-[9rem]",
                        children: /*#__PURE__*/ jsx(Input, {
                            className: "tw-w-full",
                            autoComplete: "off",
                            value: secondValue,
                            onChange: (e)=>handleInputChange(e.target.value, 1),
                            type: type
                        })
                    })
                ]
            });
        default:
            return null;
    }
}
function ConditionalSelect({ options, placeholder, onSelect, disabled, applyButtonLabel, noInputs, showValueInDropdown = false, defaultValue, type = 'text' }) {
    const { languageCode } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(defaultValue?.option);
    const [inputValues, setInputValues] = useState(defaultValue?.values || []);
    const renderOptions = options || defaultOptions.map((option)=>({
            ...option,
            label: i18n[languageCode][option.label]
        }));
    const getFormattedLabel = ()=>{
        const option = renderOptions.find((opt)=>opt.value === selectedOption);
        if (option) {
            const displayValue = 'double' === option.blockType ? `${option.label}: ${inputValues[0]} - ${inputValues[1]}` : `${option.label}: ${inputValues[0]}`;
            return displayValue;
        }
    };
    const [forcedValue, setForcedValue] = useState(getFormattedLabel() || '');
    const handleOptionSelect = (value)=>{
        setSelectedOption(value);
        setInputValues([]);
        if (noInputs) {
            onSelect?.({
                option: value,
                values: []
            });
            setIsOpen(false);
        }
    };
    const handleInputChange = (values)=>{
        setInputValues(values);
    };
    const handleApply = ()=>{
        if (selectedOption && onSelect) {
            onSelect({
                option: selectedOption,
                values: inputValues
            });
            if (showValueInDropdown) {
                const displayValue = getFormattedLabel();
                if (displayValue) setForcedValue(displayValue);
            }
        }
        setIsOpen(false);
    };
    if (noInputs) return /*#__PURE__*/ jsxs(Select, {
        onValueChange: (value)=>handleOptionSelect(value),
        disabled: disabled,
        defaultValue: defaultValue?.option?.toString(),
        children: [
            /*#__PURE__*/ jsx(SelectTrigger, {
                className: "tw-w-full",
                children: /*#__PURE__*/ jsx(SelectValue, {
                    placeholder: placeholder
                })
            }),
            /*#__PURE__*/ jsx(SelectContent, {
                className: "tw-w-full",
                children: renderOptions.filter((option)=>'single' === option.blockType).map((option)=>/*#__PURE__*/ jsx(SelectItem, {
                        value: option.value.toString(),
                        children: option.label
                    }, option.value))
            })
        ]
    });
    return /*#__PURE__*/ jsxs(Popper, {
        open: isOpen,
        onOpenChange: setIsOpen,
        children: [
            /*#__PURE__*/ jsx(PopperTrigger, {
                asChild: true,
                children: /*#__PURE__*/ jsx("div", {
                    children: /*#__PURE__*/ jsx(DropdownValue, {
                        className: "tw-flex tw-w-full tw-pointer-events-none",
                        size: "small",
                        placeholder: placeholder,
                        disabled: disabled,
                        forcedValue: showValueInDropdown ? forcedValue : void 0
                    })
                })
            }),
            /*#__PURE__*/ jsxs(PopperContent, {
                className: "tw-w-full tw-m-0",
                removePadding: true,
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: "tw-flex tw-flex-col tw-items-start tw-gap-1 tw-rounded tw-px-2.4 tw-py-1.6 tw-bg-neutral-light-100 dark:tw-bg-elevation-dark-03 tw-w-full",
                        children: renderOptions.map((option)=>/*#__PURE__*/ jsxs("div", {
                                className: "tw-w-full",
                                children: [
                                    /*#__PURE__*/ jsx(Radio, {
                                        inputElProps: {
                                            name: 'conditional-select'
                                        },
                                        className: "tw-py-0.2",
                                        size: "s",
                                        checked: selectedOption === option.value,
                                        onChange: ()=>handleOptionSelect(option.value),
                                        title: option.label
                                    }),
                                    selectedOption === option.value && /*#__PURE__*/ jsx(RadioItemInput, {
                                        blockType: option.blockType,
                                        onChange: handleInputChange,
                                        defaultValue: defaultValue?.option === option.value ? defaultValue?.values : void 0,
                                        type: type
                                    })
                                ]
                            }, option.value))
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: "tw-sticky tw-bg-neutral-light-200 tw-b-0 tw-w-full tw-px-2.4 tw-py-1.6 tw-flex tw-justify-start tw-items-center dark:tw-bg-elevation-dark-02",
                        children: /*#__PURE__*/ jsx(Button, {
                            type: "submit",
                            variant: "primary",
                            size: "xs",
                            onClick: handleApply,
                            children: applyButtonLabel || i18n[languageCode].APPLY
                        })
                    })
                ]
            })
        ]
    });
}
export { ConditionalSelect, defaultOptions };
